import { Refresh as RefreshIcon } from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { subDays } from "date-fns";
import moment from "moment";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTrips,
  type Trip,
  type TripListResponse,
} from "../../apis/customerProfile/apis";
import * as routes from "../../constants/routes";
import { strings } from "../../I18n";
import { modifyCurrencyToSymbolAndValue } from "../../utils/utils";
import CompactDateRangePicker from "../wallets/CompactDateRangePicker";
import ReceiptDrawer from "./ReceiptDrawer";

interface Props {
  userId: number;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  FINISHED: "success",
  ONGOING: "info",
  ERROR: "error",
  PAYMENT_ERROR: "error",
  WALLET_DUE: "warning",
  UNPAID: "warning",
  CANCELED_BY_USER_TIMEOUT: "default",
  AUTHENTICATING: "info",
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const CustomerTrips: React.FC<Props> = ({ userId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trips, setTrips] = useState<TripListResponse | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });
  const [selectedPaymentKey, setSelectedPaymentKey] = useState<string | null>(
    null
  );

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTrips({
        userId,
        page,
        size: rowsPerPage,
        startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
        endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
      });
      setTrips(data);
    } catch (e) {
      setError(strings("descriptions.noTripsFound"));
    } finally {
      setLoading(false);
    }
  }, [userId, page, rowsPerPage, dateRange]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = (range: {
    startDate: Date;
    endDate: Date;
    key: string;
  }) => {
    setDateRange(range);
    setPage(0);
  };

  const handleOpenReceipt = (paymentKey: string) => {
    setSelectedPaymentKey(paymentKey);
  };

  const handleCloseReceipt = () => {
    setSelectedPaymentKey(null);
  };

  return (
    <>
      <Card elevation={3}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6">
              {strings("descriptions.trips")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CompactDateRangePicker
                dateRange={dateRange}
                onChange={handleDateRangeChange}
              />
              <Tooltip title={strings("buttons.refresh")}>
                <IconButton onClick={fetchTrips} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !trips?.content?.length ? (
            <Alert severity="info">
              {strings("descriptions.noTripsFound")}
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>{strings("descriptions.dateAndTime")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.duration")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.vehicle")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Journey ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.amount")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.rating")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.status")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{strings("descriptions.receipt")}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trips.content.map((trip: Trip) => (
                      <TableRow key={trip.id} hover={true}>
                        <TableCell>
                          {moment(trip.startTime).format("DD.MM.YYYY HH:mm")}
                        </TableCell>
                        <TableCell>{formatDuration(trip.duration)}</TableCell>
                        <TableCell>
                          {trip.vehicleName || trip.vehicleCode}
                        </TableCell>
                        <TableCell>
                          <Link
                            href={`${routes.DASHBOARD_RENTALS}?rentId=${trip.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(
                                `${routes.DASHBOARD_RENTALS}?rentId=${trip.id}`
                              );
                            }}
                            underline="hover"
                            sx={{ cursor: "pointer" }}
                          >
                            #{trip.id}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {modifyCurrencyToSymbolAndValue(
                            trip.currency,
                            trip.amount / 100
                          )}
                        </TableCell>
                        <TableCell>
                          {trip.ratingGrade ? (
                            <Rating
                              value={trip.ratingGrade / 20}
                              readOnly={true}
                              size="small"
                              precision={0.5}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trip.status}
                            size="small"
                            color={statusColors[trip.status] || "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {trip.paymentKey && trip.status === "FINISHED" ? (
                            <Tooltip
                              title={strings("descriptions.viewReceipt")}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenReceipt(trip.paymentKey!)
                                }
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={trips.totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ReceiptDrawer
        open={!!selectedPaymentKey}
        paymentKey={selectedPaymentKey}
        onClose={handleCloseReceipt}
      />
    </>
  );
};

export default CustomerTrips;
